import React, { Fragment, useEffect, useState } from "react";
import HoverIconButton from "components/HoverIconButton";
import { useFormikContext } from "formik";
import { STATE_KEYS } from "utils/constants/state";
import get from "lodash/get";
import Padder from "components/Padder";
import { useSelector } from "react-redux";
import { formatProductFeatures } from "components/FormerEditor/common";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  LIST_DESTINATIONS,
  listItemDragProps,
  listDragProps
} from "utils/drag";
import { reorderFeatureProducts } from "utils/feature";
import { secondaryBackButton, BUTTON_CLASS_CONTEXT } from "utils/constants/ui";
import Button from "components/Form/fields/Button";
import cloneDeep from "lodash/cloneDeep";
import MenuNavHeader from "components/MenuNavHeader";
import Grip from "components/Grip";
import classnames from "classnames";

const FeatureListItem = ({ title, remove, draggable, snapshot }) => {
  const [hover, setHover] = useState();

  const containerProps = {
    className: classnames(
      "flex flex-row items-center justify-between pa3 bg-white bb hairline-1 bh--inset w-100 relative overflow-hidden",
      {
        "pointer": draggable,
        "bg-content--secondary": hover,
        "bg-white": !hover
      }
    ),
    onMouseEnter: () => !hover && draggable && setHover(true),
    onMouseLeave: () => hover && draggable && setHover(false)
  };

  return (
    <div {...containerProps}>
      <Grip hover={hover} snapshot={snapshot} />
      <div className="f5">{title}</div>
      <HoverIconButton
        type="close"
        tooltip="Remove from order"
        onClick={remove}
      />
    </div>
  );
};

const DraggableFeatureItem = (props) => (
  <Draggable key={props.uuid} draggableId={props.uuid} index={props.index}>
    {(provided, snapshot) => (
      <li {...listItemDragProps(provided)}>
        <FeatureListItem {...props} draggable snapshot={snapshot} />
      </li>
    )}
  </Draggable>
);

const FeatureListProductOrder = ({ name }) => {
  const { values, setFieldValue, setFieldTouched } = useFormikContext();
  const goBack = () => {
    setFieldValue(STATE_KEYS.EDITOR.FEATURE_INTENT, null);
    setFieldValue(STATE_KEYS.EDITOR.FEATURE_ACTIVE_PRODUCT, null);
  };

  const { features, products: storeProducts } = useSelector((state) => ({
    // Should be ok as its not resource specific
    // Could reference application uuid though
    features: state.getIn(["feature", "data"]).toJS(),
    products: state.getIn(["product", "data"]).toJS()
  }));

  const productsPath = `${name}.data.products`;
  const products = get(values, productsPath, []);
  const targetProductId = get(values, STATE_KEYS.EDITOR.FEATURE_ACTIVE_PRODUCT);
  const ctxProductIndex = products.findIndex(
    ({ uid }) => uid === targetProductId
  );
  let label = "All";
  let layout;
  let productFeatureLayoutPath;
  let resetCollection;
  let featureItemsLayout;

  if (ctxProductIndex > -1) {
    const ctxProduct = products[ctxProductIndex];
    label = ctxProduct.title;
    productFeatureLayoutPath = `${STATE_KEYS.MANIFEST.FEATURES}.${targetProductId}.layout`;
    layout = get(values, productFeatureLayoutPath, []);

    const config = get(values, STATE_KEYS.MANIFEST.CONFIG);
    const ctxStoreProduct = storeProducts.find(
      ({ id }) => id === ctxProduct.uid
    );
    const ctxPreceding =
      ctxProductIndex > 0 &&
      storeProducts.find(({ id }) => {
        const preProd = products[ctxProductIndex - 1];
        return preProd && id === preProd.uid;
      });

    if (ctxStoreProduct) {
      const adjustedConfig = cloneDeep(config);
      adjustedConfig.features.common.limit = null;

      const productFeatures = formatProductFeatures(
        {
          product: ctxStoreProduct,
          config: adjustedConfig,
          preceding: ctxPreceding
        },
        features
      );
      const hasProductFeatureItems =
        productFeatures &&
        productFeatures.items &&
        productFeatures.items.length;
      if (hasProductFeatureItems) {
        const computedFeatureItemsLayout = productFeatures.items.reduce(
          (memo, { uuid }) => {
            if (uuid) {
              memo.push({ uuid });
            }
            return memo;
          },
          []
        );

        resetCollection = cloneDeep(computedFeatureItemsLayout);
        featureItemsLayout = [...computedFeatureItemsLayout];
      }
    }
  }

  useEffect(() => {
    const shouldSetInitialLayout = Boolean(
      productFeatureLayoutPath &&
        layout &&
        !layout.length &&
        featureItemsLayout &&
        featureItemsLayout.length
    );

    if (shouldSetInitialLayout) {
      // Run async to avoid blocked execution
      setTimeout(
        () =>
          setFieldValue(
            productFeatureLayoutPath,
            cloneDeep(featureItemsLayout)
          ),
        250
      );
    }
  }, []);

  const buttonProps = secondaryBackButton({ onClick: () => goBack() });

  const layoutFeatureNodes = layout
    .reduce((memo, { uuid }) => {
      const featureMatch = features.find((feature) => feature.uuid === uuid);
      if (featureMatch) {
        memo.push(featureMatch);
      }
      return memo;
    }, [])
    .map((feature, featureIx) => {
      const targetIndex = layout.findIndex(({ uuid }) => feature.uuid === uuid);
      const itemProps = {
        index: featureIx,
        title: feature.title,
        uuid: feature.uuid,
        remove: () => {
          const updatedLayout = cloneDeep(layout);
          updatedLayout.splice(targetIndex, 1);
          setFieldValue(productFeatureLayoutPath, updatedLayout);
        }
      };

      return <DraggableFeatureItem key={featureIx} {...itemProps} />;
    });

  const emptyPrompt = (
    <div className="pt3">
      <div className="f4 ph3 pb3 fw5">Reset</div>
      <div className="f6 ph3 pb3 lh-copy">
        There are no features in your ordered list. Click to reset.
      </div>
      <div className="ph3 pb3">
        <Button
          theme="slim"
          onClick={() => {
            setFieldValue(productFeatureLayoutPath, resetCollection);
          }}
          classes={{
            contextClass: BUTTON_CLASS_CONTEXT.SECONDARY
          }}
          copy="Reset"
        />
      </div>
    </div>
  );

  return (
    <Fragment>
      <MenuNavHeader navSetters={[buttonProps]} label={`Order ${label}`} />
      <div className="f7 lh-title pa3 bg-content--secondary bb hairline-1 bh--inset">
        Drag and drop features to re-order them.
      </div>
      {layoutFeatureNodes.length ? (
        <DragDropContext
          onDragEnd={reorderFeatureProducts({
            layout,
            path: productFeatureLayoutPath,
            setFieldTouched,
            setFieldValue
          })}
        >
          <Droppable droppableId={LIST_DESTINATIONS.FEATURE_PRODUCT_ORDER}>
            {(provided, snapshot) => (
              <ul {...listDragProps({ provided, snapshot })}>
                {layoutFeatureNodes}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        emptyPrompt
      )}
      <Padder theme="visible" />
    </Fragment>
  );
};

export default FeatureListProductOrder;
